import { Auth } from 'aws-amplify';
import { setUser, resetUser } from './UserSlice';
import { store } from '../../app/store';
import { resetSubscription } from '../subscription/SubscriptionSlice';
import { resetBilling } from '../subscription/BillingSlice';
import { updateProfile } from '../../common/api/subscription';

/**
 * Method that signs up a user and moves the sign up process to the next phase
 * @param {Object} accountFields - User related information(firstName, lastName, email, password, confirmPassword)
 */
export const handleCreateAccount = async ({
  firstName,
  lastName,
  email,
  password,
  confirmPassword,
}) => {
  const username = email;
  const userAttributes = {
    email: email,
    given_name: firstName,
    family_name: lastName,
  };

  if (password !== confirmPassword) {
    throw Error('Passwords do not match!');
  }
  await Auth.signUp({
    username,
    password,
    attributes: userAttributes,
  });
};

/**
 * Handles logging in a user
 * @param {Object.<string, string>} credentials - Object with the user email and password
 */
export const handleLogin = async ({ username, password }) => {
  const user = await Auth.signIn(username, password).then((userObj) => {
    const user = {
      ...userObj.attributes,
    };
    return user;
  });
  if (user) {
    const formattedUser = {
      sub: user.sub,
      firstName: user.given_name,
      lastName: user.family_name,
      email: user.email,
      emailVerified: user.email_verified,
    };
    console.log('User logged in');
    store.dispatch(setUser(formattedUser));
    localStorage.setItem('userWasLoggedIn', 'true');
  } else {
    throw new Error('There was an error logging the user in');
  }
};

/**
 * Logs out the currently logged in user
 */
export const handleLogout = async () => {
  try {
    await Auth.signOut().then(() => {
      store.dispatch(resetUser());
      store.dispatch(resetSubscription());
      store.dispatch(resetBilling());
      localStorage.setItem('userWasLoggedIn', 'false');
      console.log('User has been signed out');
    });
  } catch (err) {
    console.error(err.message);
  }
};

/**
 * Handles the request of a forgot password verification code by sending an email
 * @param {String} username
 */
export const handleForgotPasswordResetCode = async (username) => {
  await Auth.forgotPassword(username);
  console.log('Forgot Password Email Sent.');
};

/**
 * Handles the submission of a password reset
 * @param {Object.<string, string, string>} values - username, authentication code, and new password
 */
export const handleForgotPasswordReset = async ({
  username,
  authenticationCode,
  newPassword,
}) => {
  await Auth.forgotPasswordSubmit(username, authenticationCode, newPassword);
  console.log('User Password changed successfully');
};

/**
 * Handler which updates user information
 * @param {Object.<string, string, string>} userInfo - Object with user attributes firstName, lastName, email
 */
export const handleUserUpdate = async ({ firstName, lastName, email }) => {
  const user = await Auth.currentAuthenticatedUser();
  let updatedValues = {};
  if (email && user.attributes.email !== email) {
    updatedValues.email = email.trim();
  }
  if (firstName && user.attributes.firstName !== firstName) {
    updatedValues.given_name = firstName.trim();
  }
  if (lastName && user.attributes.lastName !== lastName) {
    updatedValues.family_name = lastName.trim();
  }
  await Auth.updateUserAttributes(user, updatedValues);
  const updatedUser = {
    ...user.attributes,
    ...updatedValues,
  };
  if (updatedValues.email) {
    updatedUser.email_verified = false;
  }

  const formattedUser = {
    sub: updatedUser.sub,
    firstName: updatedUser.given_name,
    lastName: updatedUser.family_name,
    email: updatedUser.email,
    emailVerified: updatedUser.email_verified,
  };

  store.dispatch(setUser(formattedUser));
  console.log('User info updated.');

  //Also update Recurly with first name and last name if they were updated
  //Email will not be updated in recurly until it is confirmed
  let recurlyUpdate = {};
  if (updatedValues.given_name) {
    recurlyUpdate = {
      ...recurlyUpdate,
      first_name: updatedValues.given_name,
    };
  }
  if (updatedValues.family_name) {
    recurlyUpdate = {
      ...recurlyUpdate,
      last_name: updatedValues.family_name,
    };
  }
  await updateProfile(recurlyUpdate);
  console.log('Subscription account updated.');
};

/**
 * Confirms the account and email of a new account after initial sign up and then logs in the user
 * @param {object} userCredential - email and authentication code
 * @param {string} password - the user's password in order to log them in after confirming the account
 */
export const handleConfirmAccount = async (
  { email, authenticationCode },
  password
) => {
  await Auth.confirmSignUp(email, authenticationCode).then(async () => {
    const credentials = {
      username: email,
      password: password,
    };
    await handleLogin(credentials);
  });
};

/**
 * Method to confirm an updated email
 * @param {String} authenticationCode -authentication code to confirm email
 */
export const handleConfirmEmail = async ({ authenticationCode }) => {
  await Auth.verifyCurrentUserAttributeSubmit('email', authenticationCode).then(
    () => {
      Auth.currentAuthenticatedUser({ bypassCache: true }).then((user) => {
        const formattedUser = {
          sub: user.attributes.sub,
          firstName: user.attributes.given_name,
          lastName: user.attributes.family_name,
          email: user.attributes.email,
          emailVerified: user.attributes.email_verified,
        };
        store.dispatch(setUser(formattedUser));
        console.log('Confirmed email');
        //Now that the email update in Cognito is confirmed, also
        //update the customer's email in Recurly
        updateProfile({ email: user.attributes.email });
        console.log('Email updated in subscription account.');
      });
    }
  );
};

/**
 * Resends a signup verification code to a user
 * @param {string} email - User's email
 */
export const handleResendConfirmationCode = async (email) => {
  try {
    //Check if there is a logged in user:
    const { attributes } = await Auth.currentAuthenticatedUser();
    if (attributes.email_verified !== true) {
      //Re-verify email address:
      Auth.verifyCurrentUserAttribute('email');
    }
  } catch (err) {
    //No logged in user, account is unconfirmed, resend sign up
    Auth.resendSignUp(email);
  }
};

/**
 * Updates user's password
 * @param {Object.<string, string>} credentials - user's old and new password
 */
export const handleChangePassword = async ({ password, newPassword }) => {
  var user = await Auth.currentAuthenticatedUser();
  var result = await Auth.changePassword(user, password, newPassword);
  if (result) {
    console.log('Password successfully changed');
  } else {
    throw new Error('Error changing passwords');
  }
};
